import db from '@/rxdb/database';
import { combineLatest, firstValueFrom, map, mergeMap } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { getCachedObservable } from '@/rxdb/observablesCache';
import { groupBy } from '@/services/sanitize.service';

function mapInvoice(item) {
    if (item) {
        const plainItem = item.toMutableJSON();
        return {
            ...plainItem,
            emissionDate: plainItem.emissionDate ? new Date(plainItem.emissionDate) : null,
            createdAt: plainItem.createdAt ? new Date(plainItem.createdAt) : null,
            invoiceDate: plainItem.invoiceDate ? new Date(plainItem.invoiceDate) : null,
            conclusionDate: plainItem.conclusionDate ? new Date(plainItem.conclusionDate) : null,
            situationEmissionDate: plainItem.situationEmissionDate ? new Date(plainItem.situationEmissionDate) : null,
        };
    } else {
        return null;
    }
}
export function getInvoices(projectId) {
    return getCachedObservable('getInvoices_' + projectId, () =>
        db
            .getProjectCollections(projectId)
            .invoices.find({ sort: [{ createdAt: 'asc' }] })
            .$.pipe(map((invoices) => mapInvoiceGroups(invoices))),
    );
}

function mapInvoiceGroups(dbInvoices) {
    const invoices = dbInvoices.map(mapInvoice);
    return groupBy(invoices, 'groupId').map((versions) => {
        versions.reverse();
        return {
            ...versions[0],
            versions,
        };
    });
}

function mergeToInvoice(invoiceId, dbInvoices) {
    const versions = dbInvoices
        .map((invoice) => {
            return mapInvoice(invoice, []);
        })
        .reverse();
    const invoice = versions.find((invoice) => invoice.id === invoiceId);
    return {
        ...invoice,
        versions,
    };
}
export function getInvoice(projectId, invoiceId) {
    return db
        .getProjectCollections(projectId)
        .invoices.findOne({ selector: { id: invoiceId } })
        .$.pipe(
            mergeMap((invoice) =>
                invoice
                    ? combineLatest([
                          firstValueFrom(
                              db.getProjectCollections(projectId).invoices.find({
                                  selector: { groupId: invoice.groupId },
                                  sort: [{ createdAt: 'asc' }],
                              }).$,
                          ),
                      ])
                    : null,
            ),
        )
        .pipe(map(([invoices]) => mergeToInvoice(invoiceId, invoices)));
}

export function removeInvoice(projectId, id) {
    return db
        .getProjectCollections(projectId)
        .invoices.findOne({ selector: { id: id } })
        .remove();
}

export async function createInvoice(projectId, item) {
    const result = await db.getProjectCollections(projectId).invoices.insert({
        projectId,
        groupId: uuidv4(),
        ...item,
        emissionDate: item.emissionDate ? item.emissionDate.toISOString() : null,
    });
    return result.toJSON();
}

export async function updateInvoice(projectId, invoice) {
    const dbInvoice = await db
        .getProjectCollections(projectId)
        .invoices.findOne({ selector: { id: invoice.id } })
        .exec();
    const patch = { ...JSON.parse(JSON.stringify(invoice)) };
    if (invoice.emissionDate === null || !!invoice.emissionDate) {
        patch.emissionDate = invoice.emissionDate ? invoice.emissionDate.toISOString() : null;
    }
    if (invoice.conclusionDate === null || !!invoice.conclusionDate) {
        patch.conclusionDate = invoice.conclusionDate ? invoice.conclusionDate.toISOString() : null;
    }
    return dbInvoice.atomicPatch(patch);
}

export async function removeInvoiceGroup(projectId, invoiceGroupId) {
    const entities = await db
        .getProjectCollections(projectId)
        .invoices.find({ selector: { groupId: invoiceGroupId } })
        .exec();
    return Promise.all(entities.map((entity) => entity.remove()));
}

export function getInvoiceStatus(invoice, invoiceReviews) {
    if (!invoice.emissionDate) {
        return 'toEmit';
    } else if (invoice.conclusionDate && invoice.conclusion) {
        return invoice.conclusion;
    } else if (
        invoiceReviews.length === 0 ||
        invoiceReviews.every((review) => review.emissionDate && review.conclusion)
    ) {
        return 'toValidate';
    } else {
        return 'toReview';
    }
}
export function getSituationStatus(invoice) {
    if (!invoice.conclusionDate) {
        return 'waitingForConclusion';
    } else if (!invoice.situationEmissionDate) {
        return 'toEmit';
    } else {
        return 'emitted';
    }
}

export function getAmountTTCAlreadyChargedByAmendmentId(invoices, exceptInvoice) {
    return invoices
        .filter(
            (anInvoice) =>
                anInvoice.conclusion === 'validated' &&
                anInvoice.id !== exceptInvoice.id &&
                anInvoice.situationEmissionDate &&
                anInvoice.situationEmissionDate.getTime() <
                    (exceptInvoice.invoiceDate || exceptInvoice.emissionDate || exceptInvoice.createdAt).getTime(),
        )
        .reduce((acc, invoice) => {
            if (invoice.validatedDistributionByAmendments) {
                return [...acc, ...invoice.validatedDistributionByAmendments];
            } else {
                return acc;
            }
        }, [])
        .reduce((acc, distribution) => {
            if (acc[distribution.id]) {
                acc[distribution.id] += distribution.amountTTC;
            } else {
                acc[distribution.id] = distribution.amountTTC;
            }
            return acc;
        }, {});
}
